import React from "react";
import { arClass } from "../../../utils/useArClass";
import WaitingRoomPatientItem from "./WaitingRoomPatientItem";

/**
 * Skeleton placeholder card shown while the waiting room is loading (GridView variant)
 * @returns Skeleton JSX
 */
const GridSkeleton = ({ darkMode }) => {
  const block = darkMode ? "bg-slate-700" : "bg-slate-200";
  const card = darkMode
    ? "bg-slate-800 border-slate-700"
    : "bg-white border-slate-200";
  return (
    <>
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className={`rounded-2xl border p-5 space-y-4 max-sm:p-4 ${card}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className={`h-5 w-36 rounded ${block} animate-pulse`} />
            <div className={`h-5 w-16 rounded-full ${block} animate-pulse`} />
          </div>
          <div className={`h-3 w-24 rounded ${block} animate-pulse`} />
          <div className="space-y-2">
            <div className={`h-3 w-full rounded ${block} animate-pulse`} />
            <div className={`h-3 w-3/4 rounded ${block} animate-pulse`} />
          </div>
          <div className={`h-24 rounded-xl ${block} animate-pulse`} />
          <div className={`h-9 w-full rounded-lg ${block} animate-pulse`} />
        </div>
      ))}
    </>
  );
};

/**
 * Grid (card) view of the waiting room.
 * Props:
 * - loading: whether data is still being fetched
 * - empty: React node rendered when there are no entries
 * - items: array of pre-computed patient item descriptors
 * - viewProps: shared handlers/state object (see WaitingRoom.jsx)
 * - darkMode: boolean
 * @returns Grid JSX
 */
const GridView = ({ loading, empty, items, viewProps, darkMode }) => {
  const cardBg = darkMode ? "bg-slate-800" : "bg-white";
  const cardText = darkMode ? "text-slate-100" : "text-slate-900";

  return (
    <div
      className={arClass(
        "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6",
      )}
    >
      {loading ? (
        <GridSkeleton darkMode={darkMode} />
      ) : items.length === 0 ? (
        empty
      ) : (
        items.map((it) => (
          <WaitingRoomPatientItem
            key={it.entry.id}
            entry={it.entry}
            minutes={it.minutes}
            urgency={it.urgency}
            sectionState={it.sectionState}
            setExpandedSections={viewProps.setExpandedSections}
            assignedDoctorName={it.assignedDoctorName}
            assignedNurseName={it.assignedNurseName}
            creatorName={it.creatorName}
            paymentInfo={it.paymentInfo}
            patientName={it.patientName}
            cardBg={cardBg}
            cardText={cardText}
            darkMode={viewProps.darkMode}
            defaultLanguage={viewProps.defaultLanguage}
            statusInfo={it.statusInfo}
            statusBadge={it.statusBadge}
            urgencyBadge={it.urgencyBadge}
            selected={viewProps.selectedIds.includes(it.entry.id)}
            handleSelectItem={viewProps.handleSelectItem}
            canDeleteEntries={viewProps.canDeleteEntries}
            toggleActions={viewProps.toggleActions}
            actionsOpenId={viewProps.actionsOpenId}
            anchorEl={viewProps.anchorEl}
            onCloseActions={viewProps.onCloseActions}
            actionLoading={viewProps.actionLoading}
            showActionDescription={viewProps.showActionDescription}
            setShowActionDescription={viewProps.setShowActionDescription}
            theme={viewProps.theme}
            roles={viewProps.roles}
            isAdmin={viewProps.isAdmin}
            handleStatusChange={viewProps.handleStatusChange}
            handleDeleteEntry={viewProps.handleDeleteEntry}
            navigate={viewProps.navigate}
          />
        ))
      )}
    </div>
  );
};

export default GridView;